import { useTranslationQuery } from '@booking-portal/api/queries/use-translations-query';
import type { ITranslationWithValidationMessages } from '@booking-portal/api/services/translation-service';
import { defaultLanguage } from '@booking-portal/config/i18n-config';
import type { IResponse } from '@booking-portal/types/api';
import type { UseQueryReturnType } from '@tanstack/vue-query';
import { useQueryClient } from '@tanstack/vue-query';
import { until } from '@vueuse/core';
import { useSubscription } from '@vueuse/rxjs';
import { Subject, switchMap } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { computed, toRaw, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Downloads remote translations and caches them in the local storage.
 * If a request fails it will use the cached translations.
 */
export function useRemoteTranslations() {
  const { locale, setLocaleMessage } = useI18n();
  const language$ = new Subject<string>();
  const queryClient = useQueryClient();

  // Push every locale change into the rxjs stream. We intentionally do NOT
  // use { immediate: true }: the stream below invalidates all non-translation
  // queries, which on a fresh page load would cancel queries that just
  // resolved (e.g. /redirect-to). We only want to invalidate on real changes.
  watch(locale, (newLang) => {
    language$.next(newLang);
  });

  // Remove legacy local storage entries:
  Object.keys(localStorage)
    .filter((key) => key.startsWith('translations-'))
    .forEach((key) => localStorage.removeItem(key));

  // Default language query
  const defaultLanguageQuery = useTranslationQuery(defaultLanguage);

  // User selected query
  const userLanguageQuery = useTranslationQuery(locale);
  const isLoading = computed(
    () =>
      defaultLanguageQuery.isLoading.value || userLanguageQuery.isLoading.value
  );

  // Each time the language changes wait until all pending queries have been loaded and then invalidate all queries
  useSubscription(
    language$
      .pipe(
        distinctUntilChanged(),
        switchMap((lang) => {
          // Wait until all queries have been loaded
          return until(isLoading)
            .toBe(false)
            .then(() => lang);
        }),
        switchMap(() => {
          // Invalidate all queries except the language & setupData queries
          return queryClient.invalidateQueries({
            predicate: (query) => {
              return (
                query.queryKey[0] !== 'language' &&
                query.queryKey[0] !== 'setupData'
              );
            },
          });
        })
      )
      .subscribe()
  );

  watch(
    [locale, userLanguageQuery.data, defaultLanguageQuery.data],
    () => {
      if (defaultLanguageQuery.data.value) {
        setLoadedLanguageOrFallback(defaultLanguage, defaultLanguageQuery);
      }

      if (locale.value !== defaultLanguage && userLanguageQuery.data.value) {
        setLoadedLanguageOrFallback(locale.value, userLanguageQuery);
      }
    },
    { immediate: true }
  );

  function setLoadedLanguageOrFallback(
    locale: string,
    query: UseQueryReturnType<
      IResponse<ITranslationWithValidationMessages>,
      unknown
    >
  ) {
    const { data, error } = query;
    const translations = data.value?.data;

    if (translations && !error.value) {
      setLocaleMessage(locale, toRaw(translations));
    }
  }

  return {
    isLoading,
  };
}
